// src/config/standards/esl.js

// WIDA English Language Development (ELD) Standards Framework (2020 Edition)
// The WIDA ELD Standards are the most widely adopted ESL/ELL framework in the US,
// used in 41 states + DC, Puerto Rico, and the US Virgin Islands.
//
// Standards (ELD 1–6):
//   ELD-1: Social & Instructional Language
//   ELD-2: Language of Language Arts
//   ELD-3: Language of Mathematics
//   ELD-4: Language of Science
//   ELD-5: Language of Social Studies
//   ELD-6: Language of Health & Physical Education (added 2020)
//
// Key Language Uses (KLUs — 2020 Framework):
//   Narrate, Inform, Explain, Argue
//
// Proficiency Levels (1–6):
//   1 = Entering, 2 = Emerging, 3 = Developing,
//   4 = Expanding, 5 = Bridging, 6 = Reaching
//
// Grade Bands (official WIDA):
//   K, 1, 2–3, 4–5, 6–8, 9–12
//
// Each entry maps to a unique `id`: `ESL-<Grade>-<Code>`

const BAND_FOR_GRADE = {
  // WIDA band K
  "Kindergarten": "K",
  "Pre-K": "K",

  // WIDA band 1
  "1st Grade": "1",

  // WIDA band 2-3
  "2nd Grade": "2-3",
  "3rd Grade": "2-3",

  // WIDA band 4-5
  "4th Grade": "4-5",
  "5th Grade": "4-5",

  // WIDA band 6-8
  "6th Grade": "6-8",
  "7th Grade": "6-8",
  "8th Grade": "6-8",

  // WIDA band 9-12
  "High School": "9-12",
  "9th Grade": "9-12",
  "10th Grade": "9-12",
  "11th Grade": "9-12",
  "12th Grade": "9-12",

  // Map broader/special categories to closest WIDA band
  "Higher Education": "9-12",
  "Adult Education": "9-12",
  "Homeschool": "K",
  "Not Grade Specific": "K",
  "Functional Skills (All Levels)": "K",
  "Staff": "9-12",
};

const buildForGrade = (grade, entries) =>
  entries.map(({ code, description }) => ({
    id: `ESL-${grade.replace(/\s+/g, '')}-${code}`,
    code,
    description,
  }));

// ---- Domain content by WIDA band ----

const DOMAIN_CONTENT = {

  // ELD Standard 1 — Social & Instructional Language
  "Social & Instructional Language (ELD-1)": {
    "K": [
      { code: "WIDA.ELD1.K.Narrate.1", description: "With prompting and support, use single words or short phrases to share personal experiences or recount simple events." },
      { code: "WIDA.ELD1.K.Inform.1", description: "Name familiar people, places, and objects using vocabulary from everyday social and classroom routines." },
      { code: "WIDA.ELD1.K.Explain.1", description: "With visual or physical support, respond to simple directions and questions using gestures, actions, or one-word responses." },
    ],
    "1": [
      { code: "WIDA.ELD1.1.Narrate.1", description: "Use simple sentences and familiar vocabulary to describe personal experiences and classroom events in sequence." },
      { code: "WIDA.ELD1.1.Inform.1", description: "Identify and name classroom objects, routines, and peers using high-frequency social and academic vocabulary." },
      { code: "WIDA.ELD1.1.Explain.1", description: "Respond to and ask simple questions about daily routines using learned phrases and sentence frames." },
    ],
    "2-3": [
      { code: "WIDA.ELD1.2-3.Narrate.2", description: "Use a series of related sentences to recount personal and school events, including a beginning, middle, and end." },
      { code: "WIDA.ELD1.2-3.Inform.2", description: "Use content and social vocabulary to describe classroom rules, procedures, and interactions." },
      { code: "WIDA.ELD1.2-3.Explain.2", description: "Give and follow multi-step oral and written directions using sequence words and transition phrases." },
      { code: "WIDA.ELD1.2-3.Argue.2", description: "Express agreement or disagreement with peers using simple connectives and sentence starters." },
    ],
    "4-5": [
      { code: "WIDA.ELD1.4-5.Narrate.3", description: "Produce extended oral and written narratives about personal experiences using temporal and logical connectives." },
      { code: "WIDA.ELD1.4-5.Inform.3", description: "Present information about familiar topics using grade-appropriate academic vocabulary and visual supports." },
      { code: "WIDA.ELD1.4-5.Explain.3", description: "Explain classroom and school procedures, expectations, and outcomes using precise vocabulary and structured sentences." },
      { code: "WIDA.ELD1.4-5.Argue.3", description: "State opinions and provide reasons using linking phrases such as 'because,' 'for example,' and 'I think.'" },
    ],
    "6-8": [
      { code: "WIDA.ELD1.6-8.Narrate.4", description: "Construct cohesive narratives about personal, academic, and community experiences using varied sentence structure." },
      { code: "WIDA.ELD1.6-8.Inform.4", description: "Deliver and interpret informational messages in academic and social contexts using domain-specific vocabulary." },
      { code: "WIDA.ELD1.6-8.Explain.4", description: "Explain complex social and academic concepts using evidence, examples, and precise language." },
      { code: "WIDA.ELD1.6-8.Argue.4", description: "Engage in academic discussions, presenting and defending a viewpoint using evidence and countering opposing views." },
    ],
    "9-12": [
      { code: "WIDA.ELD1.9-12.Narrate.5", description: "Produce extended and nuanced narratives that incorporate perspective, tone, and complex sentence structure." },
      { code: "WIDA.ELD1.9-12.Inform.5", description: "Present detailed information on complex topics using academic register, hedging language, and discourse markers." },
      { code: "WIDA.ELD1.9-12.Explain.5", description: "Analyze and explain social, cultural, and academic phenomena using sophisticated vocabulary and cohesive devices." },
      { code: "WIDA.ELD1.9-12.Argue.5", description: "Construct well-reasoned arguments supported by evidence, acknowledging counterarguments and qualifying claims." },
    ],
  },

  // ELD Standard 2 — Language of Language Arts
  "Language of Language Arts (ELD-2)": {
    "K": [
      { code: "WIDA.ELD2.K.Narrate.1", description: "With support, retell key details from stories and informational texts using pictures and learned vocabulary." },
      { code: "WIDA.ELD2.K.Inform.1", description: "Identify characters, settings, and key events in a story using single words or short phrases." },
    ],
    "1": [
      { code: "WIDA.ELD2.1.Narrate.1", description: "Retell stories and informational texts using beginning-middle-end structure and high-frequency vocabulary." },
      { code: "WIDA.ELD2.1.Inform.1", description: "Describe characters and settings using adjectives and simple sentence patterns." },
      { code: "WIDA.ELD2.1.Explain.1", description: "Identify and explain the main topic of an informational text using key vocabulary." },
    ],
    "2-3": [
      { code: "WIDA.ELD2.2-3.Narrate.2", description: "Summarize narrative and informational texts using text structure and academic vocabulary." },
      { code: "WIDA.ELD2.2-3.Inform.2", description: "Describe character traits, story themes, and author's purpose using text evidence and sentence frames." },
      { code: "WIDA.ELD2.2-3.Explain.2", description: "Identify and explain cause-effect relationships and compare-contrast structures in texts." },
      { code: "WIDA.ELD2.2-3.Argue.2", description: "Express opinions about texts using basic argumentative language and simple evidence." },
    ],
    "4-5": [
      { code: "WIDA.ELD2.4-5.Narrate.3", description: "Analyze narrative elements including theme, plot structure, and character motivation using academic language." },
      { code: "WIDA.ELD2.4-5.Inform.3", description: "Summarize and compare informational texts using text features, domain vocabulary, and graphic organizers." },
      { code: "WIDA.ELD2.4-5.Explain.3", description: "Explain an author's craft including word choice, text structure, and point of view using precise language." },
      { code: "WIDA.ELD2.4-5.Argue.3", description: "Develop a written argument about a text using claims, reasons, and supporting evidence." },
    ],
    "6-8": [
      { code: "WIDA.ELD2.6-8.Narrate.4", description: "Analyze how narrative elements interact to develop theme and meaning across literary texts." },
      { code: "WIDA.ELD2.6-8.Inform.4", description: "Synthesize information from multiple texts and media using academic vocabulary and cohesive writing." },
      { code: "WIDA.ELD2.6-8.Explain.4", description: "Explain how an author's perspective and rhetorical choices shape a text's meaning and impact." },
      { code: "WIDA.ELD2.6-8.Argue.4", description: "Construct evidence-based arguments about literary and informational texts using disciplinary vocabulary." },
    ],
    "9-12": [
      { code: "WIDA.ELD2.9-12.Narrate.5", description: "Evaluate how narrative craft, structure, and style contribute to the overall meaning and effect of complex literary works." },
      { code: "WIDA.ELD2.9-12.Inform.5", description: "Critically analyze and synthesize complex informational texts, identifying bias, perspective, and purpose." },
      { code: "WIDA.ELD2.9-12.Explain.5", description: "Explain the relationship between an author's rhetorical choices and the intended audience and purpose of a text." },
      { code: "WIDA.ELD2.9-12.Argue.5", description: "Produce sophisticated written arguments about complex literary and informational texts using varied evidence and counterargument." },
    ],
  },

  // ELD Standard 3 — Language of Mathematics
  "Language of Mathematics (ELD-3)": {
    "K": [
      { code: "WIDA.ELD3.K.Inform.1", description: "Name numbers, shapes, and quantities using math vocabulary with visual and physical support." },
      { code: "WIDA.ELD3.K.Explain.1", description: "Describe mathematical actions (e.g., counting, sorting) using gestures, objects, and single words." },
    ],
    "1": [
      { code: "WIDA.ELD3.1.Inform.1", description: "Use number words, operation vocabulary, and positional language to describe mathematical relationships." },
      { code: "WIDA.ELD3.1.Explain.1", description: "Explain simple mathematical procedures using sentence frames and visual models." },
    ],
    "2-3": [
      { code: "WIDA.ELD3.2-3.Inform.2", description: "Describe properties of numbers, shapes, and data sets using precise mathematical vocabulary." },
      { code: "WIDA.ELD3.2-3.Explain.2", description: "Explain steps in mathematical problem-solving using sequence language and operation vocabulary." },
      { code: "WIDA.ELD3.2-3.Argue.2", description: "Justify mathematical choices using simple reasoning language such as 'because' and 'I know that.'" },
    ],
    "4-5": [
      { code: "WIDA.ELD3.4-5.Inform.3", description: "Describe mathematical concepts and relationships using grade-level vocabulary and visual representations." },
      { code: "WIDA.ELD3.4-5.Explain.3", description: "Explain multi-step mathematical reasoning using logical connectives and academic math language." },
      { code: "WIDA.ELD3.4-5.Argue.3", description: "Defend mathematical solutions and strategies using evidence, examples, and precise vocabulary." },
    ],
    "6-8": [
      { code: "WIDA.ELD3.6-8.Inform.4", description: "Represent and communicate mathematical information using symbols, notation, and academic language." },
      { code: "WIDA.ELD3.6-8.Explain.4", description: "Explain mathematical proofs, patterns, and generalizations using disciplinary vocabulary and logical structure." },
      { code: "WIDA.ELD3.6-8.Argue.4", description: "Construct and critique mathematical arguments using precise language, counterexamples, and logical reasoning." },
    ],
    "9-12": [
      { code: "WIDA.ELD3.9-12.Inform.5", description: "Interpret and communicate complex mathematical information using formal notation, graphs, and academic register." },
      { code: "WIDA.ELD3.9-12.Explain.5", description: "Explain abstract mathematical concepts and their real-world applications using discipline-specific language." },
      { code: "WIDA.ELD3.9-12.Argue.5", description: "Develop rigorous mathematical arguments, evaluate proofs, and justify reasoning using formal mathematical language." },
    ],
  },

  // ELD Standard 4 — Language of Science
  "Language of Science (ELD-4)": {
    "K": [
      { code: "WIDA.ELD4.K.Inform.1", description: "Name living things, objects, and natural phenomena using science vocabulary and visual support." },
      { code: "WIDA.ELD4.K.Explain.1", description: "Describe observable properties of objects and simple cause-effect relationships using gestures and single words." },
    ],
    "1": [
      { code: "WIDA.ELD4.1.Inform.1", description: "Describe basic properties of matter, living things, and natural processes using science vocabulary." },
      { code: "WIDA.ELD4.1.Explain.1", description: "Explain simple observations and investigations using sentence frames and scientific vocabulary." },
    ],
    "2-3": [
      { code: "WIDA.ELD4.2-3.Inform.2", description: "Describe characteristics of living things, matter, and Earth systems using precise science vocabulary." },
      { code: "WIDA.ELD4.2-3.Explain.2", description: "Explain cause-effect relationships and patterns observed in science investigations using academic language." },
      { code: "WIDA.ELD4.2-3.Argue.2", description: "Make claims about scientific observations using simple evidence and reasoning language." },
    ],
    "4-5": [
      { code: "WIDA.ELD4.4-5.Inform.3", description: "Present scientific information using text features, diagrams, and domain-specific vocabulary." },
      { code: "WIDA.ELD4.4-5.Explain.3", description: "Explain scientific processes, systems, and phenomena using logical connectives and precise vocabulary." },
      { code: "WIDA.ELD4.4-5.Argue.3", description: "Construct scientific arguments using data, evidence, and reasoning to support or refute a claim." },
    ],
    "6-8": [
      { code: "WIDA.ELD4.6-8.Inform.4", description: "Interpret and communicate scientific data using graphs, models, and discipline-specific academic language." },
      { code: "WIDA.ELD4.6-8.Explain.4", description: "Explain complex scientific phenomena and systems using causal language, hedging, and technical vocabulary." },
      { code: "WIDA.ELD4.6-8.Argue.4", description: "Develop evidence-based scientific arguments, evaluate competing explanations, and identify limitations in data." },
    ],
    "9-12": [
      { code: "WIDA.ELD4.9-12.Inform.5", description: "Synthesize and communicate complex scientific information from multiple sources using formal academic register." },
      { code: "WIDA.ELD4.9-12.Explain.5", description: "Explain theoretical frameworks, experimental design, and scientific reasoning using discipline-specific language." },
      { code: "WIDA.ELD4.9-12.Argue.5", description: "Construct sophisticated scientific arguments using multiple forms of evidence, addressing counterarguments and uncertainty." },
    ],
  },

  // ELD Standard 5 — Language of Social Studies
  "Language of Social Studies (ELD-5)": {
    "K": [
      { code: "WIDA.ELD5.K.Narrate.1", description: "Share information about family, community, and cultural traditions using pictures and single words." },
      { code: "WIDA.ELD5.K.Inform.1", description: "Identify community helpers, places, and events using social studies vocabulary with visual support." },
    ],
    "1": [
      { code: "WIDA.ELD5.1.Narrate.1", description: "Describe personal and community history using sequence language and high-frequency vocabulary." },
      { code: "WIDA.ELD5.1.Inform.1", description: "Name and describe geographical features, community roles, and cultural traditions using social studies vocabulary." },
    ],
    "2-3": [
      { code: "WIDA.ELD5.2-3.Narrate.2", description: "Retell historical events and biographies using chronological language and academic vocabulary." },
      { code: "WIDA.ELD5.2-3.Inform.2", description: "Describe geographic, economic, and civic concepts using maps, diagrams, and content vocabulary." },
      { code: "WIDA.ELD5.2-3.Explain.2", description: "Explain cause-effect relationships in history and geography using connective language." },
      { code: "WIDA.ELD5.2-3.Argue.2", description: "Express a viewpoint about a social studies topic using simple evidence and opinion language." },
    ],
    "4-5": [
      { code: "WIDA.ELD5.4-5.Narrate.3", description: "Construct historical narratives using primary and secondary sources, chronological language, and domain vocabulary." },
      { code: "WIDA.ELD5.4-5.Inform.3", description: "Present information about geographic, economic, and civic topics using precise vocabulary and visual supports." },
      { code: "WIDA.ELD5.4-5.Explain.3", description: "Explain the causes and effects of historical events and geographic phenomena using academic language." },
      { code: "WIDA.ELD5.4-5.Argue.3", description: "Develop a claim about a social studies topic supported by evidence from texts and data sources." },
    ],
    "6-8": [
      { code: "WIDA.ELD5.6-8.Narrate.4", description: "Analyze historical narratives for perspective, bias, and significance using disciplinary vocabulary." },
      { code: "WIDA.ELD5.6-8.Inform.4", description: "Interpret and present complex social studies information using academic language and multiple source types." },
      { code: "WIDA.ELD5.6-8.Explain.4", description: "Explain the relationships among historical, geographic, economic, and civic concepts using discipline-specific language." },
      { code: "WIDA.ELD5.6-8.Argue.4", description: "Construct evidence-based arguments about historical and contemporary issues using multiple sources." },
    ],
    "9-12": [
      { code: "WIDA.ELD5.9-12.Narrate.5", description: "Critically analyze historical narratives, evaluating perspective, reliability, and interpretation using sophisticated language." },
      { code: "WIDA.ELD5.9-12.Inform.5", description: "Synthesize complex social studies information from diverse sources using formal academic register." },
      { code: "WIDA.ELD5.9-12.Explain.5", description: "Explain the interplay of historical, geographic, economic, and political forces using nuanced disciplinary language." },
      { code: "WIDA.ELD5.9-12.Argue.5", description: "Produce well-reasoned arguments on complex social studies issues using primary sources, data, and counterargument." },
    ],
  },

  // ELD Standard 6 — Language of Health & Physical Education (added in 2020 framework)
  "Language of Health & Physical Education (ELD-6)": {
    "K": [
      { code: "WIDA.ELD6.K.Inform.1", description: "Name body parts, health practices, and physical activities using vocabulary and visual support." },
    ],
    "1": [
      { code: "WIDA.ELD6.1.Inform.1", description: "Describe healthy habits, basic body systems, and physical activities using health vocabulary." },
      { code: "WIDA.ELD6.1.Explain.1", description: "Explain simple rules for games and physical activities using sequence language and sentence frames." },
    ],
    "2-3": [
      { code: "WIDA.ELD6.2-3.Inform.2", description: "Describe health concepts, physical fitness components, and nutrition using content vocabulary." },
      { code: "WIDA.ELD6.2-3.Explain.2", description: "Explain strategies for maintaining physical and emotional well-being using academic language." },
    ],
    "4-5": [
      { code: "WIDA.ELD6.4-5.Inform.3", description: "Present health and wellness information using domain-specific vocabulary and visual representations." },
      { code: "WIDA.ELD6.4-5.Explain.3", description: "Explain the relationship between physical activity, nutrition, and overall health using precise language." },
      { code: "WIDA.ELD6.4-5.Argue.3", description: "Develop an argument for healthy lifestyle choices using evidence and reasoning language." },
    ],
    "6-8": [
      { code: "WIDA.ELD6.6-8.Inform.4", description: "Interpret and communicate health data, research, and recommendations using academic language." },
      { code: "WIDA.ELD6.6-8.Explain.4", description: "Explain the impact of lifestyle choices on physical and mental health using disciplinary vocabulary." },
      { code: "WIDA.ELD6.6-8.Argue.4", description: "Construct arguments about health policies and personal wellness decisions using evidence and academic register." },
    ],
    "9-12": [
      { code: "WIDA.ELD6.9-12.Inform.5", description: "Synthesize complex health information from multiple sources using formal academic register and technical vocabulary." },
      { code: "WIDA.ELD6.9-12.Explain.5", description: "Explain the relationships among biology, behavior, environment, and public health using sophisticated disciplinary language." },
      { code: "WIDA.ELD6.9-12.Argue.5", description: "Produce nuanced arguments about health equity, public health policy, and personal wellness using rigorous evidence." },
    ],
  },

};

// Build the full grade map for "ESL / ELL"
// Keys MUST match the exact grade strings from GRADE_LEVELS_CONFIG
// so resolveStandardGradeKeys can find them with hasKey(uiGrade)
const buildESLGrades = () => {
  const out = {};

  Object.entries(BAND_FOR_GRADE).forEach(([uiGrade, band]) => {
    out[uiGrade] = {};
    Object.keys(DOMAIN_CONTENT).forEach((domain) => {
      const bandEntries = DOMAIN_CONTENT[domain][band] || [];
      out[uiGrade][domain] = buildForGrade(uiGrade, bandEntries);
    });
  });

  return out;
};

export const ESL_STANDARDS = {
  "ESL / ELL": buildESLGrades(),
};

export default ESL_STANDARDS;